import { useRef, useState } from "react";

import Api from "lib/api/httpRequestHelper";
import {
  condicaoPadraoDoProduto,
  condicoesDoProduto,
  resolveLineDiscount,
  resolveLineEncargo,
  netUnitPrice,
} from "../utils/paymentConditionUtils";
import { resolveOrderLineFromKey } from "./resolveOrderLine";
import { mergeOrderStateAcrossBrands, saveCartForBrand } from "./useBrandCart";

function groupKeyForLine(prod) {
  return `${prod.vitrine}::${prod.linhaVenda || ""}`;
}

function joinNomes(nomes) {
  return nomes.filter(Boolean).join(", ");
}

/**
 * Monta o payload por vitrine a partir do carrinho e finaliza um pedido por vitrine.
 *
 * O carrinho é persistido por marca (useBrandCart): orderState (estado React) só reflete a
 * marca ativa no momento — trocar de marca "estaciona" o carrinho da marca anterior no
 * localStorage. Sem juntar as marcas aqui, um pedido com itens de vitrines de marcas
 * diferentes finalizava só a marca ativa e perdia (silenciosamente) as demais.
 *
 * O carrinho também só permite escolher 1 loja pro pedido inteiro (useStoreSelection.js), mas
 * cada vitrine pode ter um cliente/loja diferente vinculado — um item cuja vitrine não tem
 * cliente pra loja selecionada chega aqui com idVitrineVirtualB2bCliente nulo (ver
 * ListarVitrinesB2bPdp). Em vez de descartar isso silenciosamente, avisa o usuário explicitamente
 * (confirmação antes de enviar, e menção na mensagem de sucesso) e deixa essas vitrines pendentes
 * no carrinho pra uma finalização posterior com a loja correspondente.
 */
export default function useOrderSubmit({
  orderState,
  vitrinePaymentConditionState,
  effectiveFamilies,
  setOrderState,
  setVitrinePaymentConditionState,
  currentBrand,
  effectiveBrandKeys,
  showAlert,
  openConfirm,
  closeConfirm,
  onSuccess,
  selectedIdPessoaLoja,
  requiresStoreSelection,
}) {
  const [submitOrderLoading, setSubmitOrderLoading] = useState(false);
  // Ref (não state) pra travar reentrância de forma síncrona: o botão de enviar já fica
  // disabled via submitOrderLoading, mas isso é um setState assíncrono — um duplo clique
  // bem rápido pode dar tempo de duas chamadas de submitOrder passarem antes do primeiro
  // render com o botão desabilitado, cada uma finalizando o pedido e duplicando o pré-pedido.
  const submittingRef = useRef(false);

  const submitOrder = async () => {
    if (submittingRef.current) return;

    if (requiresStoreSelection && selectedIdPessoaLoja == null) {
      showAlert(
        "error",
        "Loja",
        "Selecione a loja para a qual este pedido será enviado.",
      );
      return;
    }

    const { fullOrderState, fullVitrinePaymentConditionState, stored } =
      mergeOrderStateAcrossBrands({
        orderState,
        vitrinePaymentConditionState,
        currentBrand,
        brandKeys: effectiveBrandKeys,
      });

    const itensPorVitrine = {};
    // vitrineKey -> nome de exibição: itens cuja vitrine não tem cliente pra loja selecionada
    // (idVitrineVirtualB2bCliente nulo) ficam de fora do envio, mas precisam ser avisados.
    const vitrinesPendentesNomes = new Map();
    let missingPaymentCondition = false;

    Object.entries(fullOrderState).forEach(([key, qty]) => {
      if (qty == null || Number(qty) <= 0) return;
      const resolved = resolveOrderLineFromKey(key, effectiveFamilies);
      if (!resolved) return;
      const { prod, fam } = resolved;
      const idVitrineVirtualB2bCliente = prod.idVitrineVirtualB2bCliente;
      if (idVitrineVirtualB2bCliente == null) {
        vitrinesPendentesNomes.set(
          prod.vitrine,
          fam.vitrineDisplayName || `Vitrine ${prod.vitrine}`,
        );
        return;
      }
      // Pré-pedido é gerado por vitrine + linha de venda: a mesma vitrine pode ter
      // itens de linhas de venda diferentes (ex.: cama e banho), e cada uma precisa
      // virar um pré-pedido separado.
      const groupKey = groupKeyForLine(prod);
      const condicoes = condicoesDoProduto(prod, fam);
      const idCondicaoPagamento =
        fullVitrinePaymentConditionState[prod.vitrine] ??
        condicaoPadraoDoProduto(prod, fam);

      if (condicoes.length > 0 && idCondicaoPagamento == null) {
        missingPaymentCondition = true;
        return;
      }

      const discountPercent = resolveLineDiscount(
        prod,
        fam,
        idCondicaoPagamento,
      );
      const encargoPercent = resolveLineEncargo(fam, idCondicaoPagamento, prod);
      const valorUnitario = netUnitPrice(prod, discountPercent, encargoPercent);

      if (!itensPorVitrine[groupKey]) {
        itensPorVitrine[groupKey] = {
          idVitrineVirtualB2bCliente,
          vitrineNome: fam.vitrineDisplayName || `Vitrine ${prod.vitrine}`,
          itens: [],
        };
      }
      // prod.idProduto pode vir como string quando o IdProduto numérico de origem é
      // 0/ausente (ver family-builder.js) — o backend espera idProduto como número
      // (int) e usa idProdutoString como alternativa textual, então mandamos os dois
      // sempre coerentes em vez de arriscar mandar string onde é esperado número.
      const idProdutoRaw = prod.idProduto ?? prod.ref;
      const idProdutoNum = Number(idProdutoRaw);
      const itemPayload = {
        idProduto: Number.isFinite(idProdutoNum) ? idProdutoNum : 0,
        idProdutoString: String(idProdutoRaw ?? prod.ref ?? ""),
        quantidade: Number(qty),
        valorUnitario: Math.round(valorUnitario * 100) / 100,
      };
      if (idCondicaoPagamento != null) {
        itemPayload.idCondicaoPagamento = Number(idCondicaoPagamento);
      }
      itensPorVitrine[groupKey].itens.push(itemPayload);
    });

    if (missingPaymentCondition) {
      showAlert(
        "error",
        "Condição de pagamento",
        "Alguns itens do pedido não possuem condição de pagamento. Abra o carrinho e selecione a condição para a vitrine correspondente.",
      );
      return;
    }

    const vitrinesComItens = Object.entries(itensPorVitrine).filter(
      ([, data]) => data.itens.length > 0,
    );

    if (vitrinesComItens.length === 0) {
      if (vitrinesPendentesNomes.size > 0) {
        showAlert(
          "error",
          "Loja",
          `A loja selecionada não está vinculada a nenhuma das vitrines do carrinho (${joinNomes(
            [...vitrinesPendentesNomes.values()],
          )}). Selecione a loja correspondente pra finalizar esses itens.`,
        );
      } else {
        showAlert(
          "error",
          "Erro",
          "Adicione itens ao pedido antes de finalizar.",
        );
      }
      return;
    }

    // Regrava, marca a marca, o carrinho sem os grupos já confirmados no servidor — usada
    // tanto no sucesso total (todos os grupos, limpa tudo) quanto na falha parcial (só os
    // confirmados ficam de fora, o resto permanece no carrinho pra reenvio).
    const persistRemaining = (confirmedGroupKeys) => {
      (effectiveBrandKeys || []).forEach((brand) => {
        const isCurrent = brand === currentBrand;
        const brandOrder = isCurrent
          ? orderState
          : stored[brand]?.order || {};
        const brandConditions = isCurrent
          ? vitrinePaymentConditionState
          : stored[brand]?.vitrinePaymentConditions || {};

        const nextOrder = {};
        Object.entries(brandOrder).forEach(([key, qty]) => {
          const resolved = resolveOrderLineFromKey(key, effectiveFamilies);
          const resolvedGroupKey = resolved
            ? groupKeyForLine(resolved.prod)
            : null;
          if (resolvedGroupKey && confirmedGroupKeys.has(resolvedGroupKey)) {
            return;
          }
          nextOrder[key] = qty;
        });
        const nextConditions = { ...brandConditions };
        confirmedGroupKeys.forEach((groupKey) => {
          const [vitrineKey] = groupKey.split("::");
          delete nextConditions[vitrineKey];
        });

        if (isCurrent) {
          setOrderState(nextOrder);
          setVitrinePaymentConditionState(nextConditions);
        } else {
          saveCartForBrand(brand, nextOrder, nextConditions, effectiveBrandKeys);
        }
      });
    };

    // Envio de fato — só roda depois da confirmação do usuário quando há vitrine pendente
    // (loja selecionada não cobre todas as vitrines do carrinho).
    const proceed = async () => {
      submittingRef.current = true;
      setSubmitOrderLoading(true);
      const resultados = [];
      const vitrinesConfirmadas = new Set();
      let erroMsg = null;
      try {
        for (const [groupKey, data] of vitrinesComItens) {
          const response = await Api.post(
            "finalize-virtual-showcase-b2b-pedido",
            "showcaseB2B",
            {
              idVitrineVirtualB2bCliente: data.idVitrineVirtualB2bCliente,
              idPessoaLoja: selectedIdPessoaLoja,
              itens: data.itens,
            },
            true,
          );
          if (
            response &&
            response.Success &&
            response.Result?.IdVitrineVirtualB2bPedido
          ) {
            resultados.push(response.Result.IdVitrineVirtualB2bPedido);
            vitrinesConfirmadas.add(groupKey);
          } else {
            erroMsg =
              response?.ErrorMessage ||
              response?.Message ||
              "Erro ao finalizar pedido.";
            break;
          }
        }

        if (erroMsg) {
          // Pedidos de vitrines já confirmadas foram commitados no servidor — tirar essas
          // do carrinho (da marca certa) antes de mostrar o erro, senão um reenvio duplica.
          if (vitrinesConfirmadas.size > 0) {
            persistRemaining(vitrinesConfirmadas);
            erroMsg = `${erroMsg} Pedido(s) #${resultados.join(", #")} já foram confirmados e removidos do carrinho.`;
          }
          showAlert("error", "Erro", erroMsg);
          return;
        }

        // Sucesso: todo grupo enviado foi confirmado — persistRemaining limpa o carrinho de
        // TODAS as marcas envolvidas (não só a ativa), menos as vitrines pendentes (loja
        // diferente), que continuam no carrinho pra uma finalização posterior.
        persistRemaining(vitrinesConfirmadas);
        onSuccess?.();
        let successMessage =
          resultados.length > 1
            ? `Seus pedidos foram enviados com sucesso! Pedidos: #${resultados.join(", #")}`
            : `Seu pedido foi enviado com sucesso! Pedido: #${resultados[0]}`;
        if (vitrinesPendentesNomes.size > 0) {
          successMessage += ` Ainda ficaram no carrinho itens de ${joinNomes(
            [...vitrinesPendentesNomes.values()],
          )} — a loja selecionada não está vinculada a ${
            vitrinesPendentesNomes.size > 1 ? "essas vitrines" : "essa vitrine"
          }. Selecione a loja correspondente pra finalizar.`;
        }
        showAlert("success", "Pedido(s) Enviado(s)!", successMessage);
      } catch (error) {
        const msg =
          typeof error === "string"
            ? error
            : error?.message || "Erro ao finalizar pedido. Tente novamente.";
        showAlert("error", "Erro", msg);
      } finally {
        submittingRef.current = false;
        setSubmitOrderLoading(false);
      }
    };

    if (vitrinesPendentesNomes.size > 0) {
      const nomesEnviando = joinNomes(
        vitrinesComItens.map(([, data]) => data.vitrineNome),
      );
      const nomesPendentes = joinNomes([...vitrinesPendentesNomes.values()]);
      openConfirm({
        title: "Loja não vinculada a todas as vitrines",
        message: `A loja selecionada está vinculada apenas a ${nomesEnviando}. Só ${
          vitrinesComItens.length > 1 ? "essas vitrines serão enviadas" : "essa vitrine será enviada"
        } agora — ${nomesPendentes} ${
          vitrinesPendentesNomes.size > 1 ? "ficam pendentes" : "fica pendente"
        } de uma finalização posterior com a loja correspondente. Deseja continuar?`,
        callback: () => {
          closeConfirm?.();
          proceed();
        },
      });
      return;
    }

    await proceed();
  };

  return { submitOrderLoading, submitOrder };
}
